import { Matrix4, Vector3 } from 'three';
import { hexToLinear } from '../../core/color';
import { SURFACE } from '../mesh/attributes';
import { createMeshBuilder, type VertexStyle } from '../mesh/meshBuilder';
import { boxSolid, octahedronSolid, prismSolid } from '../fabric/solids';
import type { WorldFeature } from '../types';
import { birdPoseAt, planBirdFlights } from './birds';
import { planRoofBirds, roofBirdPoseAt } from './roofBirds';
import { buildRoofBirdFeet } from './roofBirdFeet';
import { createBirdBatch } from './birdBatch';

/** Closed silhouettes with tapered wings, a forked tail and a small head and ochre bill. */
export function buildBirdModel() {
  const frame = { origin: [0, 0, 0] as const, yaw: 0 };
  const white: VertexStyle = {
    color: hexToLinear('#b9b69e'),
    surface: SURFACE.cloth,
    edgeId: 0,
    seed: 1538,
    extent: [1, 1],
    strokeAngle: 0,
  };
  const dark = { ...white, color: hexToLinear('#353e34') };
  const bill = { ...white, color: hexToLinear('#a3873f') };
  const closed = { top: true, bottom: true };
  const body = createMeshBuilder(),
    wing = createMeshBuilder();
  prismSolid(
    body,
    white,
    frame,
    [
      [-0.32, 0],
      [-0.12, -0.09],
      [0.19, -0.085],
      [0.29, 0],
      [0.19, 0.085],
      [-0.12, 0.09],
    ],
    -0.045,
    0.065,
    closed
  );
  octahedronSolid(body, white, frame, [0.27, 0.075, 0], 0.085);
  prismSolid(
    body,
    bill,
    frame,
    [
      [0.3, -0.028],
      [0.43, 0],
      [0.3, 0.028],
    ],
    0.045,
    0.082,
    closed
  );
  for (const side of [-1, 1])
    boxSolid(
      body,
      dark,
      frame,
      [0.282, 0.087, side * 0.056 - 0.01],
      [0.3, 0.104, side * 0.056 + 0.01],
      closed
    );
  prismSolid(
    body,
    white,
    frame,
    [
      [-0.25, -0.055],
      [-0.48, -0.09],
      [-0.4, 0],
      [-0.48, 0.09],
      [-0.25, 0.055],
    ],
    -0.015,
    0.005,
    closed
  );
  prismSolid(
    wing,
    white,
    frame,
    [
      [0.12, 0.04],
      [0.14, 0.25],
      [-0.1, 0.59],
      [-0.31, 0.53],
      [-0.18, 0.24],
      [-0.12, 0.04],
    ],
    -0.015,
    0.015,
    closed
  );
  prismSolid(
    wing,
    dark,
    frame,
    [
      [-0.1, 0.59],
      [-0.27, 0.85],
      [-0.42, 0.88],
      [-0.31, 0.53],
    ],
    -0.012,
    0.012,
    closed
  );
  /* The common cloth surface keeps the city's palette lighting and painter pass on these bodies. */
  return { body: body.build(), wing: wing.build() };
}

export const birdsFeature: WorldFeature = {
  id: 'birds',
  label: 'Waterbirds',
  weight: 0.3,
  build(ctx) {
    const flights = planBirdFlights(ctx.rng.fork('waterbirds'), ctx.layout);
    const roofBirds = planRoofBirds(
      ctx.roofs ?? [],
      ctx.layout.landmarks.map((l) => l.footprint),
      ctx.rng.fork('roof-birds')
    );
    const count = flights.length + roofBirds.length;
    const model = buildBirdModel();
    const material = ctx.materials.painted();
    const leftWing = model.wing.clone().scale(1, 1, -1);
    const index = leftWing.index!;
    for (let i = 0; i < index.count; i += 3) {
      const b = index.getX(i + 1);
      index.setX(i + 1, index.getX(i + 2));
      index.setX(i + 2, b);
    }
    const { mesh, parts } = createBirdBatch(
      [model.body, leftWing, model.wing, ...(roofBirds.length ? [buildRoofBirdFeet()] : [])],
      count,
      roofBirds.length,
      material
    );
    mesh.userData['roofBirds'] = roofBirds;
    const base = new Matrix4(),
      local = new Matrix4(),
      roll = new Matrix4(),
      scale = new Vector3(),
      foldTurn = new Matrix4();
    let sleeping = false,
      stopFlightsSince = Infinity,
      previous = 0;
    const roofClockOffsets = roofBirds.map(() => 0);
    const animated = {
      id: 'waterbird-flights',
      setTimeOfDay(_minutes: number, activity: number) {
        if (activity <= 0.05 && !sleeping) stopFlightsSince = previous;
        if (activity > 0.05) {
          if (sleeping) {
            /* A roosting bird resumes from a staggered rest, rather than joining a mid-flight phase
               of the clock that kept advancing overnight. Airborne birds finish their current motion. */
            roofBirds.forEach((bird, k) => {
              const offset = roofClockOffsets[k]!;
              if (roofBirdPoseAt(bird, previous + offset, stopFlightsSince + offset).fold === 1)
                roofClockOffsets[k] = -previous - bird.phase + (bird.phase % bird.rest) * 0.6;
            });
          }
          stopFlightsSince = Infinity;
        }
        sleeping = activity <= 0.05;
      },
      update(time: number) {
        if (time < previous) stopFlightsSince = sleeping ? time : Infinity;
        previous = time;
        flights.forEach((flight, i) => {
          const pose = birdPoseAt(flight, time);
          base
            .makeRotationY(pose.yaw)
            .multiply(roll.makeRotationX(pose.bank))
            .scale(scale.setScalar(flight.scale))
            .setPosition(...pose.position);
          mesh.setMatrixAt(parts.bodies[i]!, base);
          for (const side of [-1, 1]) {
            local.makeRotationX(pose.wing * side).setPosition(0, 0.035, 0);
            mesh.setMatrixAt(
              (side === -1 ? parts.leftWings : parts.rightWings)[i]!,
              local.premultiply(base)
            );
          }
        });
        roofBirds.forEach((bird, k) => {
          const i = flights.length + k;
          const offset = roofClockOffsets[k]!;
          const pose = roofBirdPoseAt(bird, time + offset, stopFlightsSince + offset);
          base
            .makeRotationY(pose.yaw)
            .multiply(roll.makeRotationX(pose.bank))
            .scale(scale.setScalar(bird.scale))
            .setPosition(...pose.position);
          mesh.setMatrixAt(
            parts.bodies[i]!,
            local.copy(base).scale(scale.set(1, 1 + pose.fold * 0.9, 1))
          );
          for (const side of [-1, 1]) {
            /* The prism's positive plan v becomes negative world z, so the right wing folds with
               positive yaw. Compacting its flight chord keeps the feathers alongside the body. */
            local
              .makeRotationX(pose.wing * side)
              .multiply(
                foldTurn
                  .makeRotationY(side * pose.fold * 1.35)
                  .scale(scale.set(1 - 0.6 * pose.fold, 1, 1 - 0.45 * pose.fold))
              )
              .setPosition(0, 0.035, 0);
            mesh.setMatrixAt(
              (side === -1 ? parts.leftWings : parts.rightWings)[i]!,
              local.premultiply(base)
            );
          }
          local.copy(base).scale(scale.setScalar(0.001 + 0.999 * pose.fold));
          mesh.setMatrixAt(parts.feet[k]!, local);
        });
      },
    };
    animated.update(0);
    return {
      objects: [mesh],
      animated: [animated],
      obstacles: [],
      counts: {
        birds: count,
        rooftopBirds: roofBirds.length,
        birdMeshes: 1,
      },
    };
  },
};
